<script lang="ts">
	/**
	 * The per-product funnel (§12.10) — the panel that answers "which of these actually sells".
	 *
	 * A table rather than a chart, because there are six measures per product and the comparison
	 * that matters is between rows on one of them at a time. It is sortable for the same reason:
	 * the most-viewed product and the best-converting one are routinely different, and that gap is
	 * the finding.
	 *
	 * Conversion is **purchases over viewing visits**, not over views: a customer who read the page
	 * four times is one person deciding, and dividing by the four would report the store as
	 * converting a quarter as well as it does.
	 */

	import {
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell
	} from 'flowbite-svelte';
	import { EmptyState, Panel } from '../../../components';
	import { formatAmount } from '../../../stores/storefront';
	import type { AnalyticsProductRow, AnalyticsQuery } from '../../../AppTypes';
	import { getAnalyticsProducts } from '../../../AppNetwork';

	export let filter: AnalyticsQuery;

	type SortKey = 'views' | 'viewingSessions' | 'engagedSessions' | 'checkouts' | 'purchases' | 'conversionRate';

	let rows: AnalyticsProductRow[] = [];
	let error = '';
	let loading = false;
	let sortKey: SortKey = 'views';
	let descending = true;

	$: queryKey = JSON.stringify(filter);
	$: void fnLoad(queryKey);

	async function fnLoad(_key: string): Promise<void> {
		loading = true;
		try {
			rows = (await getAnalyticsProducts(filter, 100)).items;
			error = '';
		} catch (caught) {
			error = caught instanceof Error ? caught.message : 'Failed to load product analytics';
			rows = [];
		} finally {
			loading = false;
		}
	}

	function fnSort(key: SortKey): void {
		if (sortKey === key) {
			descending = !descending;
			return;
		}
		sortKey = key;
		descending = true;
	}

	/*
	 * Ties break on the name, for the reason the catalogue's own listing does: two products sharing
	 * a figure that swap places between loads read as somebody else editing the store.
	 */
	$: sorted = [...rows].sort((left, right) => {
		const difference = right[sortKey] - left[sortKey];
		const ordered = descending ? difference : -difference;
		return ordered !== 0 ? ordered : left.productName.localeCompare(right.productName);
	});

	function fnMoney(amounts: { currency: string; amount: number }[]): string {
		if (amounts.length === 0) return '—';
		return amounts
			.map(entry => $formatAmount(entry.amount, entry.currency) ?? `${entry.amount} ${entry.currency}`)
			.join(' · ');
	}

	/**
	 * A conversion rate, to one decimal — and to two below one per cent.
	 *
	 * Storefront conversion lives between about one and five per cent, so one decimal is the
	 * resolution a change is actually visible at. Below one per cent a single decimal rounds every
	 * distinct rate to the same figure, which reads as a metric that never moves.
	 */
	function fnPercent(fraction: number): string {
		return `${(fraction * 100).toFixed(fraction < 0.01 ? 2 : 1)}%`;
	}

	const COLUMNS: { key: SortKey; label: string; hint: string }[] = [
		{ key: 'views', label: 'Views', hint: 'Product page views' },
		{ key: 'viewingSessions', label: 'Visitors', hint: 'Visits that viewed it' },
		{ key: 'engagedSessions', label: 'Engaged', hint: 'Visits that started the flow on it' },
		{ key: 'checkouts', label: 'Reached payment', hint: 'Visits that got to the provider' },
		{ key: 'purchases', label: 'Purchases', hint: 'Completed and paid' },
		{ key: 'conversionRate', label: 'Conversion', hint: 'Purchases over visits that viewed it' }
	];
</script>

<Panel title="Products" faIcon="box-open">
	{#if error.length > 0}
		<EmptyState variant="error" title="Product analytics could not be loaded." description={error} />
	{:else if loading && rows.length === 0}
		<EmptyState variant="loading" />
	{:else if rows.length === 0}
		<EmptyState
			variant="empty"
			title="No product was viewed in this range."
			description="Product views are recorded on /Product/:slug. A store with traffic and none of these has collection switched off."
		/>
	{:else}
		<div class="overflow-x-auto" class:opacity-50={loading}>
			<Table class="dataTable">
				<TableHead>
					<TableHeadCell>Product</TableHeadCell>
					{#each COLUMNS as column (column.key)}
						<TableHeadCell class="text-right">
							<button
								type="button"
								class="hover:underline"
								title={column.hint}
								on:click={() => fnSort(column.key)}
							>
								{column.label}
								{#if sortKey === column.key}
									<i class="fa-solid fa-{descending ? 'arrow-down' : 'arrow-up'} text-[10px] ml-1" />
								{/if}
							</button>
						</TableHeadCell>
					{/each}
					<TableHeadCell class="text-right">Revenue</TableHeadCell>
				</TableHead>
				<TableBody>
					{#each sorted as row (row.productUUID)}
						<TableBodyRow>
							<TableBodyCell class="dataTable">
								<!-- Plain text: the catalogue has no per-product address, so a link only landed at the foot of the page. -->
								{row.productName}
								{#if row.familySlug !== null}
									<span class="text-default text-xs block">{row.familySlug}</span>
								{/if}
							</TableBodyCell>
							<TableBodyCell class="dataTable text-right tabular-nums">{row.views.toLocaleString()}</TableBodyCell>
							<TableBodyCell class="dataTable text-right tabular-nums">{row.viewingSessions.toLocaleString()}</TableBodyCell>
							<TableBodyCell class="dataTable text-right tabular-nums">{row.engagedSessions.toLocaleString()}</TableBodyCell>
							<TableBodyCell class="dataTable text-right tabular-nums">{row.checkouts.toLocaleString()}</TableBodyCell>
							<TableBodyCell class="dataTable text-right tabular-nums">{row.purchases.toLocaleString()}</TableBodyCell>
							<TableBodyCell class="dataTable text-right tabular-nums">{fnPercent(row.conversionRate)}</TableBodyCell>
							<TableBodyCell class="dataTable text-right tabular-nums">{fnMoney(row.revenue)}</TableBodyCell>
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>
		</div>
	{/if}
</Panel>
